import { getAccessToken, setAccessToken, getRefreshToken, setRefreshToken, clearTokens } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function refreshTokens(): Promise<boolean> {
  try {
    const refreshTok = getRefreshToken();
    if (!refreshTok) return false;

    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: refreshTok }),
    });
    if (!res.ok) return false;
    const json = await res.json();
    // Unwrap { success, data } envelope
    const data = json?.data || json;
    if (data.accessToken) {
      setAccessToken(data.accessToken);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestInit = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...options,
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  let res = await fetch(url, config);

  // Auto-refresh on 401
  if (res.status === 401 && token) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      const newToken = getAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
      }
      config.headers = headers;
      res = await fetch(url, config);
    } else {
      clearTokens();
      window.location.href = '/auth/login';
      throw new ApiError(401, 'Session expired');
    }
  }

  if (!res.ok) {
    let errorData: ApiResponse;
    try {
      errorData = await res.json();
    } catch {
      errorData = { error: res.statusText };
    }
    throw new ApiError(res.status, errorData.error || errorData.message || 'Request failed', errorData);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  const json = await res.json();
  // Unwrap { success, data } envelope — return data directly
  if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
    return json.data as T;
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>('GET', path, undefined, options),

  post: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>('POST', path, body, options),

  put: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>('PUT', path, body, options),

  patch: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>('PATCH', path, body, options),

  delete: <T>(path: string, options?: RequestInit) =>
    request<T>('DELETE', path, undefined, options),

  upload: async <T>(file: File, path: string = '/uploads'): Promise<T> => {
    const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
    const token = getAccessToken();

    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let res = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });

    // Auto-refresh on 401
    if (res.status === 401 && token) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        const newToken = getAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
        }
        res = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: formData,
        });
      } else {
        clearTokens();
        window.location.href = '/auth/login';
        throw new ApiError(401, 'Session expired');
      }
    }

    if (!res.ok) {
      let errorData: ApiResponse;
      try {
        errorData = await res.json();
      } catch {
        errorData = { error: res.statusText };
      }
      throw new ApiError(res.status, errorData.error || errorData.message || 'Upload failed', errorData);
    }

    const json = await res.json();
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  },
};

export { ApiError };
export type { ApiResponse };
